class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.body = document.querySelector('body');
    this.productGridFiltersContainer = document.querySelector('[data-product-grid-filters-container]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search] .search__results-element');
    this.searchTerm = '';
    this.deviceIsDesktop = window.innerWidth >= 750;
  }

  connectedCallback() {
    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    // Remove the results when they are no longer relevant for the new search term
    // so they don't show up when the dropdown opens again
    if (!this.searchTerm) this.querySelector('#search-results')?.remove();

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFocus() {
    if (this.getAttribute('results') === 'true') this.open();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) return;

      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getSearchResults(searchTerm) {
    this.open();
    this.setAttribute('loading', true);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=search-results`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-search-results').innerHTML;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    // prevent page to be scrollable while searching for UX reasons
    this.body.style.overflow = 'hidden';

    /**
     * Since on desktop devices the container of the filters and the search input is sticky positioned
     * and has a z-index value lower than the products grid itself in order to
     * prevent from showing them on top of the cards, here the value of the container
     * is increased in order to be able to display the results correctly
     */
    if (this.deviceIsDesktop && this.productGridFiltersContainer) this.productGridFiltersContainer.style.zIndex = '999';
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    // restore body overflow when closing results
    this.body.style.overflow = 'visible';

    // restore filters and search input container z-index value
    if (this.deviceIsDesktop && this.productGridFiltersContainer) this.productGridFiltersContainer.style.zIndex = '0';
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
  }
}

customElements.define('predictive-search', PredictiveSearch);
